import { object, ref, string } from 'yup';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
	setRegistrationStatus,
	getRegistrationStatus,
	setAuthorizationStatus,
	setShowAfterRegisterPreorderWarning,
} from '@/stores/slices/popupSlice';
import { Button, Icon, InputInLabel, Select } from '@/components/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { setUserId, setUserType } from '@/stores/slices/userSlice';
import axios from 'axios';
import { useEffect, useState } from 'react';
import baseUrl from '@/services/apiConfig';
import { Checkbox, LoadingButton } from '../../ui';
import useLadyService from '@/services/LadyService';
import { Popup } from '../index';
import showToast from '../../toast/Toast';
import ConfirmPhone from '../../confirmPhone/ConfirmPhone';
import { makeRequest } from '../../../services/makeRequest';

const RegistrationPopup = (props) => {
	const { dataName } = props;

	const { dispatch, navigate, lang, t } = useLadyService();

	const [formErrors, setFormErrors] = useState({});
	const [phoneValue, setPhoneValue] = useState('');

	const formSchema = object().shape({
		email: string().email().required(t('emailisnes')),
		phone: string().test(
			'phone-is-empty',
			t('phonenumerrequired'),
			function () {
				return !!phoneValue.replace('+', '');
			},
		),
		password: string().min(8, t('min8')).required(),
		password_confirmation: string()
			.test('passwords-match', t('passdonot'), function (value) {
				return value === this.parent.password;
			})
			.required(t('required')),
	});
	const [time, setTime] = useState(60);

	const {
		register,
		handleSubmit,
		reset,
		getValues,
		setValue,
		setError,
		formState: { errors },
		clearErrors,
		trigger,
	} = useForm({
		mode: 'all',
		resolver: yupResolver(formSchema, { abortEarly: false }),
	});

	let baseOptions = [
		{ id: 'CZ', title: 'CZ +420', value: '+420', icon: 'CZ' },
		{ id: 'SK', title: 'SK +421', value: '+421', icon: 'SK' },
		{ id: 'DE', title: 'DE +49', value: '+49', icon: 'DE' },
		// {id: 'RU', title: "RU +7", value: '+7', icon: "RU"},
		{ id: 'UA', title: 'UA +380', value: '+380', icon: 'UA' },
		// { id: 'US', title: "US +1", value: '+1', icon: "US"},
		{ id: 'PL', title: 'PL +48', value: '+48', icon: 'PL' },
		{ id: 'AT', title: 'AT +43', value: '+43', icon: 'AT' },
		{ id: 'IT', title: 'IT +39', value: '+39', icon: 'IT' },
		{ id: 'HU', title: 'HU +36', value: '+36', icon: 'HU' },
	];

	const [phoneInputComplete, setPhoneInputComplete] = useState(false);
	const [code, setCode] = useState('');
	const [codeError, setCodeError] = useState(false);
	const [codeStatus, setCodeStatus] = useState(false);
	const [showCodeInput, setShowCodeInput] = useState(false);
	const [showCodeButton, setShowCodeButton] = useState(false);
	const [canSubmit, setCanSubmit] = useState(false);
	const [codesList, setCodesList] = useState(baseOptions);

	const fetchData = async () => {
		setLoadingCode(true);

		try {
			const route = `auth/send-code`;
			const method = 'POST';
			const payload = {
				phone: (countryCode + phoneValue).replace(/[" "+]/g, ''),
			};

			const { result, message } = await makeRequest({
				route,
				method,
				payload,
			});

			if (!result) {
				if (message === 'sms_send_failed') {
					showToast({
						message: t('phoneconfirmincorrect'),
						variant: 'error',
					});
				}
			} else {
				setShowCodeInput(true);
				setShowCodeButton(false);
			}
			setLoadingCode(false);
		} catch (error) {
			console.error('error: ', error);
			setLoadingCode(false);

			showToast({
				message: t('oops'),
				variant: 'error',
			});
		}
	};

	const fetechCodes = async () => {
		try {
			const route = `services/countries`;
			const method = 'GET';

			const { countries } = await makeRequest({ route, method });
			if (countries.length < 1) {
				console.info('no codes: ', countries);
			} else {
				const filterIds = new Set(baseOptions?.map((item) => item?.id));
				const mappedList = Object.keys(countries).map((key) => {
					const p_code = countries[key]?.phone_code;
					const tippy = countries[key]?.name;
					return {
						id: key?.toUpperCase(),
						title: `${key} +${p_code}`?.toUpperCase(),
						value: `+${p_code}`?.toUpperCase(),
						icon: key?.toUpperCase(),
						tippy: tippy,
					};
				});
				const filteredList = mappedList
					.filter((item) => {
						if (
							!filterIds?.has(item.id) &&
							item.id !== 'RU' &&
							item.id !== 'UA' &&
							item.id !== 'KZ' &&
							!(
								item.value === '+1' &&
								item.id !== 'US' &&
								item.id !== 'CA'
							)
						) {
							return item;
						}
					})
					.sort((a, b) => {
						return (
							parseInt(a?.value?.replace('+', '')) -
							parseInt(b?.value?.replace('+', ''))
						);
					});
				const firstOptionsFetched = mappedList.filter((item) =>
					filterIds?.has(item.id),
				);
				if (
					dataName === 'agency' ||
					dataName === 'indi' ||
					dataName === 'massagebrothel'
				) {
					setCodesList(
						firstOptionsFetched.filter((item) => item.id !== 'UA'),
					);
				} else {
					setCodesList([...firstOptionsFetched, ...filteredList]);
				}
			}
		} catch (error) {
			console.error('error: ', error);

			showToast({
				message: t('oops'),
				variant: 'error',
			});
		}
	};

	useEffect(() => {
		fetechCodes();
	}, []);

	useEffect(() => {
		if (phoneInputComplete) {
			setShowCodeButton(true);
		} else {
			if (canSubmit) {
				setCanSubmit(false);
			}
			if (showCodeButton) {
				setShowCodeButton(false);
			}
			if (showCodeInput) {
				setShowCodeInput(false);
			}
			if (codeStatus) {
				setCodeStatus(false);
			}
		}
	}, [phoneInputComplete]);

	const onClose = () => {
		reset();
		setPhoneValue('');
		dispatch(setRegistrationStatus(false));
		setFormErrors({});
	};

	const registrationState = useSelector(getRegistrationStatus);
	const [loading, setLoading] = useState(false);
	const [loadingCode, setLoadingCode] = useState(false);

	const sendCode = () => {
		fetchData();
	};

	const changeText = () => {
		switch (dataName) {
			case 'indi':
				return t('indireg');

			case 'agency':
				return t('agenci1');

			case 'massagebrothel':
				return t('massagebrothel');

			case 'photograph':
				return t('photographer');

			default:
				return t('client');
		}
	};

	const uuiCode = localStorage.getItem('uui');
	const [termsError, setTermsError] = useState(false);

	const onSubmit = (data, e) => {
		try {
			if (e) {
				e?.preventDefault();
			}
		} catch (error) {
			console.error('=> error', error);
			return;
		}

		if (!checked) {
			setTermsError(false);
			setTermsError(true);
			return;
		}

		if (code.length !== 4) {
			setCodeError(true);
			return;
		}
		if (!phoneValue) {
			return;
		}

		setLoading(true);
		axios
			.post(`${baseUrl}auth/register`, {
				user_type: dataName === 'massagebrothel' ? 'agency' : dataName,
				phone: (countryCode + phoneValue).replace(/[" "+]/g, ''),
				email: data.email,
				password: data.password,
				code: code,
				...(uuiCode && { publish_code: uuiCode }),
				...(dataName === 'massagebrothel' && { massage: '1' }),
			})
			.then((response) => {
				if (response.status === 201) {
					localStorage.removeItem('uui');
					const profileSlug = response.data.slug;
					axios
						.post(`${baseUrl}auth/login`, {
							identity: data.email,
							password: data.password,
						})
						.then((response) => {
							if (response.status === 200) {
								const token = response.data.token;
								localStorage.setItem('user', token);
								axios
									.request({
										headers: {
											Authorization: `Bearer ${token}`,
										},
										method: 'GET',
										url: `${baseUrl}user/me`,
									})
									.then((response) => {
										if (response.status === 200) {
											const userType =
												response.data.data.user_type;
											const userId =
												response.data.data.id;

											dispatch(setUserId(userId));
											if (userType) {
												dispatch(setUserType(userType));

												localStorage.setItem(
													'check_terms',
													'unchecked',
												);
												dispatch(
													setShowAfterRegisterPreorderWarning(
														true,
													),
												);

												if (profileSlug) {
													navigate(
														`/${lang}/lk/profile/${profileSlug}/edit`,
													);
												} else {
													if (userType === 'indi') {
														navigate(
															`/${lang}/lk/profiles`,
														);
													} else {
														navigate(
															`/${lang}/lk/${userType}`,
														);
													}
												}
											}

											try {
												const currentToken =
													localStorage.getItem(
														'user',
													);

												const arrFavorites = JSON.parse(
													localStorage.getItem(
														'favorites',
													),
												);

												if (!!arrFavorites.length) {
													axios
														.request({
															headers: {
																Authorization: `Bearer ${currentToken}`,
															},
															method: 'POST',
															url: `${baseUrl}user/favorites/import`,
															data: {
																profiles:
																	arrFavorites,
															},
														})
														.then((response) => {
															if (
																response.status ===
																200
															) {
																localStorage.removeItem(
																	'favorites',
																);
															}
														})
														.catch((error) => {
															console.error(
																error,
															);
															const res =
																error.responce;

															showToast({
																message:
																	t('oops'),
																subMessage: `server error ${res?.status}`,
																variant: `error`,
															});
														});
												}
											} catch (error) {}
										}
									})
									.catch((error) => {
										console.error(error);
										const res = error.responce;

										showToast({
											message: t('oops'),
											subMessage: `server error ${res?.status}`,
											variant: `error`,
										});
									});
								onClose();
							}
						})
						.catch((error) => {
							console.error(error);
							const res = error.responce;

							showToast({
								message: t('oops'),
								subMessage: `server error ${res?.status}`,
								variant: `error`,
							});
						});
				}
			})
			.catch((error) => {
				const errorStatus = error.response.status;
				const message = error.response.data.message;

				if (errorStatus === 400) {
					if (message === 'verify_code_not_exist') {
						setFormErrors({
							login: {
								type: 'is-email-or-phone',
								message: t('codeisincorrect'),
							},
						});
					} else {
						setFormErrors({
							login: {
								type: 'is-email-or-phone',
								message: t('userexist'),
							},
						});
					}
				} else if (message === 'code_not_valid') {
					setFormErrors({
						login: {
							type: 'is-email-or-phone',
							message: t('codeisincorrect'),
						},
					});
				} else {
					showToast({
						message: t('oops'),
						subMessage: `server error ${errorStatus}`,
						variant: 'error',
					});
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const navigateToAuthorisation = () => {
		reset();
		dispatch(setAuthorizationStatus(true));
		dispatch(setRegistrationStatus(false));
	};

	const [checked, setChecked] = useState(false);
	const onChangeTherms = (event) => {
		setChecked(event.target.checked);
	};
	useEffect(() => {
		if (checked) {
			setTermsError(false);
		}
	}, [checked]);
	let countryOptions =
		dataName === 'agency' ||
		dataName === 'indi' ||
		dataName === 'massagebrothels'
			? codesList
			: codesList;

	const [countryCode, setCountryCode] = useState(baseOptions[0].value);

	const handleKeyDown = async (event) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			const valid = await trigger();
			if (valid) {
				handleSubmit(onSubmit)(event);
			}
		}
	};

	useEffect(() => {
		document.addEventListener('keypress', (e) => {
			handleKeyDown(e);
		});
	}, []);

	return (
		<Popup
			id={'registration'}
			open={!!registrationState}
			setOpen={onClose}
			onSubmit={handleSubmit(onSubmit)}
			closeOutside={false}
			clazz={'gap-12'}
		>
			<div className="popup-form__inner text-center gap-0">
				<h2>{t('registration')}</h2>
				<p>{changeText()}</p>
			</div>
			<div className="popup-form__inner">
				{codeStatus !== 'success' && (
					<div className="select-code-phone-group">
						<fieldset>
							<legend>{t('country')}</legend>
							<Select
								arrowSize={'s'}
								type={'flag'}
								defaultTitle={countryOptions[0].title}
								options={countryOptions}
								value={countryCode}
								onChange={setCountryCode}
								isTitled={true}
							/>
						</fieldset>

						<fieldset>
							<legend>{<span>{t('phonenumber')}</span>}</legend>
							<InputInLabel
								clazz={`${errors.phone ? ' error' : ''}`}
								id={'phoneRegistration'}
								title={t('pass')}
								type={'tel'}
								required={true}
								register={{ ...register('phone') }}
								autocomplete={'tel'}
								maskValue={phoneValue}
								setMaskValue={setPhoneValue}
								setError={setError}
								errorType={'phone'}
								setInputComplete={setPhoneInputComplete}
								onChange={() => {
									if (errors.phone) {
										clearErrors('phone');
									}
									if (formErrors?.login?.message) {
										setFormErrors({});
									}
								}}
								countryCode={countryCode}
							></InputInLabel>
						</fieldset>
					</div>
				)}

				{codeStatus !== 'success' && (
					<LoadingButton
						isLoading={loadingCode}
						size={'l'}
						buttonType={'button'}
						clazz={'button--green'}
						onClick={() => sendCode()}
						disabled={!showCodeButton}
					>
						{t('getsmscode')}
					</LoadingButton>
				)}

				{showCodeInput && (
					<ConfirmPhone
						setCanSubmit={setCanSubmit}
						time={time}
						setTime={setTime}
						setShowCodeInput={setShowCodeInput}
						codeStatus={codeStatus}
						setFormCodeError={setCodeError}
						telephone={(countryCode + phoneValue).replace(
							/[" "+]/g,
							'',
						)}
						isAuth={true}
						setCode={setCode}
						error={codeError}
						setCodeStatus={setCodeStatus}
					/>
				)}
				{codeStatus === 'success' && (
					<>
						<span className="color-green text-center p1">
							{t('phonesuccess')}{' '}
							<Icon
								size={'s'}
								spritePath={'check'}
							/>
						</span>

						<fieldset>
							<legend>
								{(errors.email && (
									<span className={'input-label__error'}>
										{errors.email.message}
									</span>
								)) || <span>Email</span>}
							</legend>
							<InputInLabel
								clazz={`${errors.email ? ' error' : ''}`}
								id={'loginRegistration'}
								type={'email'}
								placeholder={'example@google.com'}
								register={{ ...register('email') }}
								autocomplete={'email'}
								required={true}
								onChange={() => {
									if (errors.email) {
										clearErrors('email');
									}
									if (formErrors?.login?.message) {
										setFormErrors({});
									}
								}}
							></InputInLabel>
						</fieldset>

						<fieldset>
							<legend>{t('pass')}</legend>
							<InputInLabel
								clazz={`${errors.password ? ' error' : ''}`}
								id={'passRegistration'}
								type={'password'}
								required={true}
								placeholder={'••••••••'}
								register={{ ...register('password') }}
								autocomplete={'new-password'}
								onChange={(e) => {
									if (errors.password) {
										clearErrors('password');
									}
									if (formErrors?.login?.message) {
										setFormErrors({});
									}
									setValue('password', e.target.value);
								}}
							/>
						</fieldset>

						{errors?.password?.message && (
							<span className={'popup-form__error'}>
								{errors.password.message}
							</span>
						)}

						<fieldset>
							<legend>{t('repeatpassword')}</legend>
							<InputInLabel
								clazz={`${
									errors.password_confirmation ? ' error' : ''
								}`}
								id={'passConfirmRegistration'}
								required={true}
								type={'password'}
								placeholder={'••••••••'}
								register={{
									...register('password_confirmation'),
								}}
								autocomplete={'new-password'}
								onChange={(e) => {
									if (errors.password_confirmation) {
										clearErrors('password_confirmation');
									}
									if (formErrors?.login?.message) {
										setFormErrors({});
									}
									setValue(
										'password_confirmation',
										e.target.value,
									);
								}}
							/>
						</fieldset>

						{errors?.password_confirmation?.message && (
							<span className={'popup-form__error'}>
								{errors.password_confirmation.message}
							</span>
						)}

						<Checkbox
							id={'agreeWithTherms'}
							clazz={`width-max align-self-center ${
								termsError ? ' error' : ''
							}`}
							checked={checked}
							onChange={onChangeTherms}
							title={t('iagree')}
						/>

						<p className={'m-0 text-center'}>
							<Link
								target={'_blank'}
								className={'hover-line color-green'}
								to={`/${lang}/rules`}
							>
								{t('termsofserviceagree')}
							</Link>{' '}
							<br></br>
							{t('and')}
							<br></br>
							<Link
								target={'_blank'}
								className={'hover-line color-green'}
								to={`/${lang}/gdpr`}
							>
								{t('privacypolicy')}
							</Link>
						</p>

						<LoadingButton
							isLoading={loading}
							disabled={
								errors.password_confirmation || !canSubmit
							}
							size={'l'}
							buttonType={'submit'}
							clazz={'button--green'}
						>
							{t('register')}
						</LoadingButton>

						{formErrors?.login && (
							<span className={'popup-form__error'}>
								{formErrors.login?.message}
							</span>
						)}
					</>
				)}
			</div>

			{codeStatus !== 'success' && (
				<>
					<hr />
					<div className="popup-form__box p-0 popup-form__bottom-link">
						<span className={'text-center fz-12 color-500'}>
							{t('haveaccount')}
						</span>
						<button
							type={'button'}
							onClick={() => navigateToAuthorisation()}
						>
							{t('login')}
						</button>
					</div>
				</>
			)}
			<p className="m-auto mt-10 capitalize">
				{t('needhelp')}?{' '}
				<a
					href={`https://wa.me/420774231617?text=Hello! I have a question / problem on website (User agent: ${navigator.userAgent})`}
					target="_blank"
				>
					{t('support')}
				</a>
				.
			</p>
		</Popup>
	);
};
export default RegistrationPopup;
